import * as React from "react";
import { Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, InfoPopover, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, Details, Section, StateView, formatRelative, useConfirm, useToast } from "@ozpanel/ui";
import { useMailboxApi, type Filter, type Vacation } from "../api";

// "Mail rules" and "Away message", ported from v1's mailbox.html.
//
// The rule editor is a WHOLE-SET replace, exactly as v1 does it, because that is what the
// handler is: PUT /mail/me/filters takes {filters:[...]} and replaces everything. Editing one
// rule therefore means sending all of them back, and a UI that sent only the edited one would
// silently delete the rest. That is stated here because it is the kind of thing a later
// refactor "tidies up" into a bug.

const FIELDS = [
  { value: "from", label: "From" },
  { value: "to", label: "To" },
  { value: "subject", label: "Subject" },
  { value: "cc", label: "Cc" },
];
const OPS = [
  { value: "contains", label: "contains" },
  { value: "is", label: "is exactly" },
  { value: "begins", label: "begins with" },
  { value: "ends", label: "ends with" },
];
const ACTIONS = [
  { value: "fileinto", label: "Move to folder" },
  { value: "discard", label: "Delete it" },
  { value: "keep", label: "Keep in Inbox" },
];

export function Rules() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const { notify } = useToast();

  const q = useQuery({ queryKey: ["filters"], queryFn: api.filters });
  const [draft, setDraft] = React.useState<Filter[] | null>(null);
  const rules = draft ?? q.data?.items ?? [];
  const dirty = draft !== null;

  const save = useMutation({
    mutationFn: (list: Filter[]) => api.replaceFilters(list),
    onSuccess: () => {
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["filters"] });
      notify({ kind: "success", title: "Your rules were saved" });
    },
    onError: (e: unknown) =>
      notify({ kind: "error", title: "Could not save your rules", body: e instanceof Error ? e.message : String(e) }),
  });

  const edit = (i: number, patch: Partial<Filter>) =>
    setDraft(rules.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const add = () =>
    setDraft([...rules, { name: "", field: "from", op: "contains", value: "", action: "fileinto", target: "" }]);
  const remove = (i: number) => setDraft(rules.filter((_, n) => n !== i));

  return (
    <div className="space-y-4">
      <Section
        title="Mail rules"
        hint={`${rules.length} rule${rules.length === 1 ? "" : "s"}`}
        actions={
          <div className="flex gap-2">
            <Button size="sm" onClick={add}>Add rule</Button>
            {dirty && (
              <>
                <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                  Discard
                </Button>
                <Button size="sm" variant="accent" onClick={() => save.mutate(rules)} disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save rules"}
                </Button>
              </>
            )}
          </div>
        }
      >
        <p className="pb-3 text-[13px] text-muted">Rules run in order on every message as it arrives.</p>
        <StateView loading={q.isLoading} error={q.error} errorContext="your rules">
          {rules.length === 0 ? (
            <div className="px-4 py-6 text-center text-[13px] text-muted">
              No rules. Mail is delivered to your Inbox unless the spam filter moves it.
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((r, i) => (
                // A rule reads as a sentence — If [field] [condition] [value], then [action] — laid out
                // as labelled fields that stack on a phone instead of one row of seven controls.
                <div key={i} className="rounded-lg border border-line bg-bg2 p-3">
                  <div className="flex items-start gap-2">
                    <label className="min-w-0 flex-1 text-[12.5px] text-muted">
                      Name (optional)
                      <input value={r.name ?? ""} onChange={(e) => edit(i, { name: e.target.value })} className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent" />
                    </label>
                    <Button size="sm" variant="ghost" className="mt-5" aria-label="Take this rule out (until you save)" onClick={() => remove(i)}>
                      <Trash2 size={16} aria-hidden />
                    </Button>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <label className="text-[12.5px] text-muted">
                      If
                      <select value={r.field} onChange={(e) => edit(i, { field: e.target.value })} className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent">
                        {FIELDS.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-[12.5px] text-muted">
                      Condition
                      <select value={r.op} onChange={(e) => edit(i, { op: e.target.value })} className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent">
                        {OPS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-[12.5px] text-muted">
                      Value
                      <input value={r.value} onChange={(e) => edit(i, { value: e.target.value })} className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent font-mono" />
                    </label>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <label className="text-[12.5px] text-muted">
                      Then
                      <select value={r.action} onChange={(e) => edit(i, { action: e.target.value })} className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent">
                        {ACTIONS.map((a) => (
                          <option key={a.value} value={a.value}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {r.action === "fileinto" && (
                      <label className="text-[12.5px] text-muted">
                        Folder
                        <input value={r.target ?? ""} onChange={(e) => edit(i, { target: e.target.value })} className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent" />
                      </label>
                    )}
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-[13px] text-muted">
                    <input type="checkbox" checked={r.stop ?? false} onChange={(e) => edit(i, { stop: e.target.checked })} />
                    Stop here if this rule matches
                  </label>
                </div>
              ))}
            </div>
          )}
        </StateView>
      </Section>

      <AwayMessage />
    </div>
  );
}

function AwayMessage() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const { notify } = useToast();
  // The reply as it stands, so it is shown before it is saved over. Until it has loaded — or when it
  // could not be read — the form is not offered: saved over an unread reply, an empty form erases it.
  const q = useQuery({ queryKey: ["vacation"], queryFn: api.vacation });
  const [edit, setEdit] = React.useState<Vacation | null>(null);
  const v: Vacation = edit ?? {
    enabled: !!q.data?.enabled,
    subject: q.data?.subject ?? "",
    message: q.data?.message ?? "",
    start_date: q.data?.start_date || null,
    end_date: q.data?.end_date || null,
  };
  const setV = (n: Vacation) => setEdit(n);
  const save = useMutation({
    mutationFn: () => api.setVacation(v),
    onSuccess: () => {
      notify({ kind: "success", title: v.enabled ? "Away message is on" : "Away message is off" });
      setEdit(null);
      void qc.invalidateQueries({ queryKey: ["vacation"] });
    },
    onError: (e: unknown) =>
      notify({ kind: "error", title: "Could not save", body: e instanceof Error ? e.message : String(e) }),
  });

  return (
    <Section
      title="Away message"
      actions={
        <Button size="sm" variant="accent" onClick={() => save.mutate()} disabled={save.isPending || !q.data}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      }
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="your away message">
      <div className="space-y-3">
        <p className="text-[13px] text-muted">Sent once to each person who writes to you.</p>
        <Details summary="Who does not get it">
          <p>It is sent once per person, not once per message, and never to mailing lists or anything that looks automated.</p>
        </Details>
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={v.enabled}
            onChange={(e) => setV({ ...v, enabled: e.target.checked })}
          />
          Send an away message while I am out
        </label>
        <label className="block text-[12.5px] text-muted">
          Subject
          <input
            value={v.subject}
            onChange={(e) => setV({ ...v, subject: e.target.value })}
            placeholder="e.g. Out of office"
            className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-3 py-2 text-[13.5px] text-fg outline-none focus:border-accent"
          />
        </label>
        <label className="block text-[12.5px] text-muted">
          Message
          <textarea
            value={v.message}
            onChange={(e) => setV({ ...v, message: e.target.value })}
            className="mt-1 block h-28 w-full resize-y rounded-lg border border-line2 bg-bg px-3 py-2 text-[13.5px] text-fg outline-none focus:border-accent"
          />
        </label>
        <div className="flex flex-wrap gap-3">
          <label className="text-[12.5px] text-muted">
            From
            <input
              type="date"
              value={v.start_date ?? ""}
              onChange={(e) => setV({ ...v, start_date: e.target.value || null })}
              className="mt-1 block rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg"
            />
          </label>
          <label className="text-[12.5px] text-muted">
            Until
            <input
              type="date"
              value={v.end_date ?? ""}
              onChange={(e) => setV({ ...v, end_date: e.target.value || null })}
              className="mt-1 block rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg"
            />
          </label>
        </div>
      </div>
      </StateView>
    </Section>
  );
}

// Held mail and spam levels live together, because a held message is the CONSEQUENCE of a level
// and neither is interpretable alone — v1 puts them on the same page for the same reason.
export function SpamAndHeld() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const { notify } = useToast();
  const confirm = useConfirm();

  const spam = useQuery({ queryKey: ["spam"], queryFn: api.spam });
  const held = useQuery({ queryKey: ["quarantine"], queryFn: api.quarantine });
  const lists = useQuery({ queryKey: ["me-spam-lists"], queryFn: api.spamLists, retry: false });

  const [mine, setMine] = React.useState<{ spam: string; reject: string } | null>(null);
  const [sender, setSender] = React.useState("");
  const cur = mine ?? {
    spam: spam.data?.mine?.spam_score == null ? "" : String(spam.data.mine.spam_score),
    reject: spam.data?.mine?.reject_score == null ? "" : String(spam.data.mine.reject_score),
  };

  const save = useMutation({
    // Empty means null means "follow my domain" — a real value with a meaning, not an omission.
    mutationFn: () =>
      api.setSpam(cur.spam.trim() === "" ? null : Number(cur.spam), cur.reject.trim() === "" ? null : Number(cur.reject)),
    onSuccess: () => {
      setMine(null);
      void qc.invalidateQueries({ queryKey: ["spam"] });
      notify({ kind: "success", title: "Your filtering level was saved" });
    },
    onError: (e: unknown) =>
      notify({ kind: "error", title: "Could not save", body: e instanceof Error ? e.message : String(e) }),
  });

  const act = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      notify({ kind: "success", title: label });
      void qc.invalidateQueries({ queryKey: ["quarantine"] });
      void qc.invalidateQueries({ queryKey: ["spam"] });
    } catch (e) {
      notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) });
    }
  };

  const addSender = async (t: "allow" | "block") => {
    const v = sender.trim();
    if (!v) return;
    try {
      await api.addSpamSender(t, v);
      setSender("");
      notify({ kind: "success", title: t === "allow" ? "Will always be accepted" : "Will always be rejected" });
      void qc.invalidateQueries({ queryKey: ["me-spam-lists"] });
    } catch (e) {
      notify({ kind: "error", title: "Could not add that sender", body: e instanceof Error ? e.message : String(e) });
    }
  };
  const askRemove = (which: "accepted" | "rejected", it: { id: number; value: string }) =>
    confirm.ask(
      {
        title: `Remove ${it.value}?`,
        body: `Mail from it is no longer always ${which}; the filter decides as usual.`,
        confirmLabel: "Remove",
        destructive: false,
      },
      () => delSender(it.id),
    );
  const delSender = async (id: number) => {
    try {
      await api.deleteSpamSender(id);
      void qc.invalidateQueries({ queryKey: ["me-spam-lists"] });
      notify({ kind: "success", title: "Removed" });
    } catch (e) {
      notify({ kind: "error", title: "Could not remove it", body: e instanceof Error ? e.message : String(e) });
    }
  };

  const cols: DataListColumn<import("../api").HeldMessage>[] = [
    { key: "sender", label: "From", sortable: true },
    { key: "subject", label: "Subject", sortable: true, phone: "title", render: (h) => h.subject || <span className="text-muted">(no subject)</span> },
    {
      key: "reason",
      label: "Why",
      // The REASON it was held is what a person can act on; a score is a number they cannot
      // interpret. The endpoint gives the reason, and it was not being shown at all.
      render: (h) => h.reason || <span className="text-muted">spam score</span>,
    },
    {
      key: "arrived_at",
      label: "Held",
      sortable: true,
      value: (h) => h.arrived_at ?? 0,
      render: (h) => (h.arrived_at ? formatRelative(new Date(h.arrived_at * 1000).toISOString()) : "—"),
    },
  ];

  // The interlock: your own level means nothing without the one it falls back to.
  const level = (l: { spam_score: number | null; reject_score: number | null } | undefined) =>
    l == null ? "—" : `${l.spam_score ?? "—"} / ${l.reject_score ?? "—"}`;

  // v1's "What the filter did with your mail". The /spam response carries an `activity` object,
  // and v2 was fetching it and throwing it away — the single most reassuring thing on the page,
  // because it turns "is spam filtering even on?" into five numbers.
  const a = (spam.data?.activity ?? null) as
    | { scanned?: number; delivered?: number; sent_to_junk?: number; refused?: number; note?: string }
    | null;

  return (
    <div className="space-y-4">
      {confirm.dialog}
      {/* HELD MAIL FIRST: a person opens this page to get a message back. The digest e-mail and the
          Overview tile both link here for exactly that. */}
      <Section title="Held for you" hint={held.data ? `${held.data.total}` : undefined}>
        <p className="text-[13px] leading-relaxed text-muted">Messages the filter held instead of delivering.</p>
        <Details summary="Deliver or delete — what each does" className="mt-1">
          <p>Deliver puts it in your Inbox and teaches the filter it was real mail. Delete removes it and teaches the filter it was spam.</p>
        </Details>
        {held.data?.incomplete && <p className="mt-2 text-[12.5px] leading-relaxed text-warn">{held.data.incomplete}</p>}
        <div className="mt-3">
          <DataList
            items={held.data?.items ?? []}
            columns={cols}
            getRowKey={(h) => String(h.id)}
            loading={held.isLoading}
            error={held.error}
            errorContext="your held mail"
            emptyMessage="Nothing is being held for you."
            rowActions={(h) => (
              <>
                <Button size="sm" variant="ghost" onClick={() => void act("Delivered — reported as not spam", () => api.releaseHeld(h.id))}>
                  Deliver
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() =>
                    confirm.ask(
                      {
                        title: "Delete this held message?",
                        body: `${h.subject || "(no subject)"} — from ${h.sender}. It cannot be got back, and the filter learns it was spam.`,
                        confirmLabel: "Delete",
                      },
                      () => act("Deleted — reported as spam", () => api.deleteHeld(h.id)),
                    )
                  }
                >
                  Delete
                </Button>
              </>
            )}
          />
        </div>
      </Section>

      <Section title="What the filter did">
        <StateView loading={spam.isLoading} error={spam.error} errorContext="your filter activity">
          {!a || !a.scanned ? (
            <div className="rounded-lg border border-line bg-bg2 px-4 py-6 text-center text-[13px] text-muted">
              {a?.note || "Nothing has arrived for you recently."}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { n: a.scanned, l: "arrived", tone: "" },
                  { n: a.delivered, l: "delivered", tone: "text-ok" },
                  { n: a.sent_to_junk, l: "to Junk", tone: "text-warn" },
                  { n: a.refused, l: "refused", tone: "text-crit" },
                ].map((f) => (
                  <div key={f.l} className="rounded-lg border border-line bg-bg2 px-3 py-2.5 text-center">
                    <div className={`text-[20px] font-[650] tabular ${f.tone}`}>{f.n ?? 0}</div>
                    <div className="mt-0.5 text-[12px] text-muted">{f.l}</div>
                  </div>
                ))}
              </div>
              {a.note && <div className="mt-2 text-[12.5px] text-muted">{a.note}</div>}
            </>
          )}
        </StateView>
      </Section>

      <Section title="How strict filtering is">
        <StateView loading={spam.isLoading} error={spam.error} errorContext="your spam settings">
          <div className="space-y-3">
            {/* What governs you stays visible: an empty box FOLLOWS the domain's number, shown beside it. */}
            <p className="text-[13px] text-muted">Leave a box empty to follow your domain's level.</p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-[12.5px] text-muted">
                <span className="flex items-center gap-1">
                  Move to Junk above
                  <InfoPopover title="Spam score">
                    Every message is given a <strong>spam score</strong> as it arrives — higher means more spam-like. Above this
                    number it goes to <strong>Junk</strong> instead of your Inbox. Typical: <code>6</code>. Lower catches more
                    (and risks a few real messages in Junk); higher lets more through.
                  </InfoPopover>
                </span>
                <input
                  value={cur.spam}
                  inputMode="decimal"
                  onChange={(e) => setMine({ ...cur, spam: e.target.value })}
                  placeholder={String(spam.data?.domain?.spam_score ?? "")}
                  className="mt-1 block w-24 rounded-lg border border-line2 bg-bg px-2.5 py-2 font-mono text-[13px] outline-none focus:border-accent"
                />
              </label>
              <label className="text-[12.5px] text-muted">
                <span className="flex items-center gap-1">
                  Refuse above
                  <InfoPopover title="Refuse score">
                    Above this higher number a message is <strong>refused outright</strong> — not even Junk — and the sender is
                    told. Keep it well above the Junk number.
                  </InfoPopover>
                </span>
                <input
                  value={cur.reject}
                  inputMode="decimal"
                  onChange={(e) => setMine({ ...cur, reject: e.target.value })}
                  placeholder={String(spam.data?.domain?.reject_score ?? "")}
                  className="mt-1 block w-24 rounded-lg border border-line2 bg-bg px-2.5 py-2 font-mono text-[13px] outline-none focus:border-accent"
                />
              </label>
              <Button variant="accent" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              {mine && (
                <Button variant="ghost" onClick={() => setMine(null)}>
                  Discard
                </Button>
              )}
            </div>
            {/* Every level that governs you, because "yours is empty" is only meaningful next to the
                number it falls back to. Junk / Refuse. */}
            <div className="flex flex-wrap gap-2 text-[12px]">
              <span className="rounded border border-line bg-bg2 px-2 py-1">
                Yours <span className="font-mono text-accent">{level(spam.data?.mine)}</span>
              </span>
              <span className="rounded border border-line bg-bg2 px-2 py-1">
                Your domain <span className="font-mono">{level(spam.data?.domain)}</span>
              </span>
              <span className="rounded border border-line bg-bg2 px-2 py-1">
                Default <span className="font-mono">{level(spam.data?.platform)}</span>
              </span>
            </div>
            {spam.data?.note && (
              <Details summary="How the levels work">
                <p>{spam.data.note}</p>
              </Details>
            )}
          </div>
        </StateView>
      </Section>

      <Section
        title="Senders you always accept or reject"
        hint={lists.data ? `${(lists.data.allow ?? []).length + (lists.data.block ?? []).length}` : undefined}
      >
        <div className="space-y-3">
          <p className="flex items-center gap-1 text-[13px] leading-relaxed text-muted">
            An address or a whole domain, for your mailbox only.
            <InfoPopover title="Always accept / reject">
              An <strong>accept</strong> entry means mail from that address or domain is never treated as spam for you, even when
              it scores high. A <strong>reject</strong> entry refuses it outright. Your domain may add its own rules too.
            </InfoPopover>
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-0 flex-[1_1_14rem] text-[12.5px] text-muted">
              Sender or domain
              <input
                value={sender}
                type="text"
                inputMode="email"
                autoCapitalize="none"
                onChange={(e) => setSender(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void addSender("allow")}
                placeholder="someone@example.com or example.com"
                className="mt-1 block w-full rounded-lg border border-line2 bg-bg px-2.5 py-2 text-[13px] text-fg outline-none focus:border-accent"
              />
            </label>
            <Button variant="accent" disabled={!sender.trim()} onClick={() => void addSender("allow")}>
              Always accept
            </Button>
            <Button disabled={!sender.trim()} onClick={() => void addSender("block")}>
              Always reject
            </Button>
          </div>
          <StateView loading={lists.isLoading} error={lists.error} errorContext="your senders">
            <div className="grid gap-3 sm:grid-cols-2">
              <SenderList title="Always accepted" tone="ok" items={lists.data?.allow ?? []} askRemove={(it) => askRemove("accepted", it)} />
              <SenderList title="Always rejected" tone="crit" items={lists.data?.block ?? []} askRemove={(it) => askRemove("rejected", it)} />
            </div>
          </StateView>
        </div>
      </Section>
    </div>
  );
}

// One side of the personal allow/block list — the accepted senders or the rejected ones. Kept
// tiny and side-by-side so the two are read as a pair, not a single ambiguous list.
function SenderList({
  title,
  tone,
  items,
  askRemove,
}: {
  title: string;
  tone: "ok" | "crit";
  items: { id: number; value: string }[];
  askRemove: (it: { id: number; value: string }) => void;
}) {
  return (
    <div className="rounded-lg border border-line">
      <div className={`border-b border-line px-3 py-1.5 text-[11.5px] font-medium ${tone === "ok" ? "text-ok" : "text-crit"}`}>
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="px-3 py-2 text-[12px] text-muted">None yet.</div>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-[13px]">
              <span className="min-w-0 font-mono [overflow-wrap:anywhere]">{it.value}</span>
              <Button size="sm" variant="ghost" onClick={() => askRemove(it)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
